import { supabase } from '../../../lib/supabase'
import { Company, CompanyFilters, PaginatedResult } from '../types'

export async function searchCompanies(
  filters: CompanyFilters
): Promise<PaginatedResult<Company>> {
  const { search, category, duplicateStatus, page, pageSize, sortField, sortDir } = filters

  let query = supabase.from('companies').select('*', { count: 'exact' })

  // Text search across key fields
  if (search.trim()) {
    const term = `%${search.trim()}%`
    query = query.or(
      `company_name.ilike.${term},email_1.ilike.${term},tel_no.ilike.${term},website.ilike.${term},co_reg_no.ilike.${term},contact_1.ilike.${term}`
    )
  }

  // Category filter
  if (category) {
    query = query.eq('category', category)
  }

  // Duplicate status filter
  if (duplicateStatus === 'duplicates') {
    query = query.not('duplicate_group_id', 'is', null)
  } else if (duplicateStatus === 'no_duplicates') {
    query = query.is('duplicate_group_id', null)
  }

  // Sorting
  query = query.order(sortField, { ascending: sortDir === 'asc', nullsFirst: false })

  // Pagination
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1
  query = query.range(from, to)

  const { data, error, count } = await query

  if (error) {
    console.error('Error searching companies:', error)
    throw new Error('Failed to search companies')
  }

  const total = count || 0
  return {
    data: (data || []) as Company[],
    total,
    page,
    pageSize,
    totalPages: Math.ceil(total / pageSize),
  }
}

export async function getCompany(id: string): Promise<Company | null> {
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    console.error('Error fetching company:', error)
    return null
  }
  return data as Company
}

export async function createCompany(
  company: Partial<Company>
): Promise<Company> {
  const { data: userData } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('companies')
    .insert({
      ...company,
      created_by: userData?.user?.id || null,
      updated_by: userData?.user?.id || null,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating company:', error)
    throw new Error('Failed to create company')
  }
  return data as Company
}

export async function updateCompany(
  id: string,
  updates: Partial<Company>
): Promise<Company> {
  const { data: userData } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('companies')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
      updated_by: userData?.user?.id || null,
    })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating company:', error)
    throw new Error('Failed to update company')
  }
  return data as Company
}

export async function deleteCompany(id: string): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting company:', error)
    throw new Error('Failed to delete company')
  }
}

export async function getCategories(): Promise<string[]> {
  const { data, error } = await supabase
    .from('companies')
    .select('category')
    .not('category', 'is', null)
    .not('category', 'eq', '')

  if (error) {
    console.error('Error fetching categories:', error)
    return []
  }

  const unique = Array.from(new Set((data || []).map(r => r.category).filter(Boolean)))
  unique.sort()
  return unique as string[]
}

export async function getCompanyCount(): Promise<number> {
  const { count, error } = await supabase
    .from('companies')
    .select('*', { count: 'exact', head: true })

  if (error) {
    console.error('Error getting company count:', error)
    return 0
  }
  return count || 0
}

export async function getDuplicateGroupMembers(groupId: string): Promise<Company[]> {
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .eq('duplicate_group_id', groupId)
    .order('original_line_number', { ascending: true })

  if (error) {
    console.error('Error fetching duplicate group:', error)
    return []
  }
  return (data || []) as Company[]
}
